/**
 * Framework-agnostic "a new build is available" store (E9 §10.3). Two
 * independent sources feed it — belt-and-suspenders per the guideline:
 * - `'sw'`: the installed service worker found a new precache (Workbox's
 *   `onNeedRefresh`, `registerServiceWorker.ts`).
 * - `'server'`: the `deploy:broadcast` WS push reports a different
 *   `BUILD_VERSION` than this tab loaded (`DeployUpdateToast.tsx`).
 *
 * Whichever fires first wins — `notifyUpdateAvailable` is a no-op once
 * `available` is already true, so the UI shows exactly ONE toast (E9: "Exactly
 * one update-toast component") no matter how many times either source fires.
 *
 * No React import here on purpose — `DeployUpdateToast.tsx` subscribes via
 * `useSyncExternalStore`, but the store itself has no framework dependency
 * (mirrors `@xdocs/ws-protocol`'s `WsClient` being framework-agnostic too).
 */

export type UpdateSource = 'sw' | 'server';

export interface UpdateState {
  available: boolean;
  source: UpdateSource | null;
  /** The `deploy:broadcast` version string, when `source === 'server'`. */
  version: string | null;
}

type Listener = (state: UpdateState) => void;

const INITIAL_STATE: UpdateState = { available: false, source: null, version: null };

let state: UpdateState = INITIAL_STATE;
let applyFn: (() => void | Promise<void>) | null = null;
const listeners = new Set<Listener>();

function setState(next: UpdateState): void {
  state = next;
  for (const listener of listeners) listener(state);
}

export function getUpdateState(): UpdateState {
  return state;
}

export function onUpdateStateChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** First notifier wins — a second source firing after the toast is already showing changes nothing. */
export function notifyUpdateAvailable(source: UpdateSource, version?: string): void {
  if (state.available) return;
  setState({ available: true, source, version: version ?? null });
}

/**
 * Registered by `registerServiceWorker.ts` once `virtual:pwa-register`'s
 * `updateSW(reloadPage)` function exists, so `applyUpdate()` below can route
 * through it (activates the waiting SW + reloads) instead of a plain
 * `location.reload()` — which would still fetch fresh hashed assets but
 * wouldn't hand control to the new SW until the NEXT load.
 */
export function setApplyUpdate(fn: (() => void | Promise<void>) | null): void {
  applyFn = fn;
}

/** Applies the pending update: the registered SW's `updateSW(true)` if available, else a plain reload. */
export async function applyUpdate(): Promise<void> {
  if (applyFn) {
    await applyFn();
    return;
  }
  window.location.reload();
}

/** Test-only: resets all module-level state between tests. */
export function __resetUpdateStoreForTests(): void {
  state = INITIAL_STATE;
  applyFn = null;
  listeners.clear();
}
